import { useEffect, useRef, useState } from "react";
import { BadgeCheck, Heart, Mail, Phone, Search, Sparkles, UserCheck, UserPlus, Users, X } from "lucide-react";

import { api, assetUrl } from "@/lib/api";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Loader from "@/components/ui/loader";
import { Pagination } from "@/components/ui/pagination";
import { EmptyPanel, PageHero, PagePanel, PageShell, PanelHeading } from "@/components/interior-page";
import { cn, titleCase } from "@/lib/utils";

const PAGE_SIZE = 16;

/** The public card a BL advisor sees of a peer. Account activity stays
 *  server-side for non-admins; email + phone come only with the profile. */
interface PeerAdvisor {
  id: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
  bio: string | null;
  verified: boolean;
  follower_count: number;
  is_following: boolean;
  is_self: boolean;
}

interface PeerProfile extends PeerAdvisor {
  following_count: number;
  email: string | null;
  phone: string | null;
}

type FollowState = { follower_count: number; is_following: boolean };

interface AdvisorStats {
  total: number;
  verified: number;
  new_30d: number;
  followers: number;
  following: number;
}

const SORTS = [
  { key: "", label: "Newest" },
  { key: "followers", label: "Most followed" },
  { key: "name", label: "Name (A–Z)" },
] as const;

const fullName = (a: { first_name: string; last_name: string }) => titleCase(`${a.first_name} ${a.last_name}`);
const initials = (a: { first_name: string; last_name: string }) =>
  `${a.first_name?.[0] ?? ""}${a.last_name?.[0] ?? ""}`.toUpperCase();
const followersLabel = (n: number) => `${n.toLocaleString()} follower${n === 1 ? "" : "s"}`;

/**
 * Advisor-to-advisor directory: a card per BL advisor with their photo, name,
 * bio and follower count, and a Follow button. Admins get the full record
 * directory instead (UserDirectory decides which one renders).
 */
export default function AdvisorPeerDirectory() {
  const toast = useToast();
  const [rows, setRows] = useState<PeerAdvisor[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("");
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [profile, setProfile] = useState<PeerProfile | null>(null);
  const [stats, setStats] = useState<AdvisorStats | null>(null);
  const requestSeq = useRef(0);
  const appliedSearch = useRef("");

  async function fetchRows(targetPage: number, opts: { search?: string; sort?: string } = {}) {
    const effSearch = opts.search ?? search;
    const effSort = opts.sort ?? sort;
    const seq = ++requestSeq.current;
    appliedSearch.current = effSearch;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        skip: String((targetPage - 1) * PAGE_SIZE),
        limit: String(PAGE_SIZE),
      });
      if (effSearch) params.set("search", effSearch);
      if (effSort) {
        params.set("sort", effSort);
        params.set("order", effSort === "name" ? "asc" : "desc");
      }
      const data = await api.get<{ total_elements: number; data: PeerAdvisor[] }>(`/directory/advisors?${params}`);
      if (seq !== requestSeq.current) return;
      setRows(data.data);
      setTotal(data.total_elements);
      setPage(targetPage);
    } catch {
      if (seq === requestSeq.current) toast.error("Failed to load advisors");
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }

  useEffect(() => {
    fetchRows(1);
    api.get<AdvisorStats>("/directory/advisors/stats").then(setStats).catch(() => {});
    // Clears the sidebar's "new advisors" badge, same as the admin directory.
    api.patch("/directory/advisors/mark-seen", {}).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Search as you type, once typing pauses.
  useEffect(() => {
    if (search === appliedSearch.current) return;
    const timer = setTimeout(() => fetchRows(1, { search }), 400);
    return () => clearTimeout(timer);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  function applyFollow(id: string, next: FollowState) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...next } : r)));
    setProfile((prev) => (prev && prev.id === id ? { ...prev, ...next } : prev));
  }

  async function toggleFollow(a: PeerAdvisor) {
    setPendingId(a.id);
    try {
      const path = `/directory/advisors/${a.id}/follow`;
      const next = a.is_following ? await api.delete<FollowState>(path) : await api.post<FollowState>(path, {});
      applyFollow(a.id, next);
      if (next.is_following !== a.is_following) {
        setStats((prev) => prev && { ...prev, following: prev.following + (next.is_following ? 1 : -1) });
      }
    } catch {
      toast.error(a.is_following ? "Couldn't unfollow" : "Couldn't follow");
    } finally {
      setPendingId(null);
    }
  }

  async function openProfile(a: PeerAdvisor) {
    try {
      setProfile(await api.get<PeerProfile>(`/directory/advisors/${a.id}/profile`));
    } catch {
      toast.error("Failed to load advisor profile");
    }
  }

  const followButton = (a: PeerAdvisor, className?: string) =>
    a.is_self ? (
      <div className={cn("flex h-10 items-center justify-center rounded-xl text-xs font-semibold text-muted-foreground", className)}>
        This is you
      </div>
    ) : (
      <Button
        variant={a.is_following ? "outline" : "default"}
        disabled={pendingId === a.id}
        onClick={(e) => {
          e.stopPropagation();
          toggleFollow(a);
        }}
        className={className}
      >
        {a.is_following ? <UserCheck /> : <UserPlus />}
        {a.is_following ? "Following" : "Follow"}
      </Button>
    );

  return (
    <PageShell>
      <PageHero
        eyebrow="BL Advisors"
        title="Connect with fellow BL Advisors."
        description="Browse the advisor network, read their bios, and follow the advisors you want to keep up with."
        aside={
          <div>
            <div className="text-[0.72rem] font-bold uppercase tracking-[0.22em] text-primary/70">Network snapshot</div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <HeroStat icon={Users} label="Advisors" value={stats?.total} />
              <HeroStat icon={BadgeCheck} label="Verified" value={stats?.verified} />
              <HeroStat icon={Sparkles} label="New (30 days)" value={stats?.new_30d} />
              <HeroStat icon={Heart} label="Your followers" value={stats?.followers} />
            </div>
            <div className="mt-3 text-sm text-muted-foreground">
              You follow{" "}
              <span className="font-bold text-foreground">{stats ? stats.following.toLocaleString() : "—"}</span>{" "}
              advisor{stats?.following === 1 ? "" : "s"}
            </div>
          </div>
        }
      />

      <PagePanel>
        <PanelHeading
          title="Advisor network"
          description={`${total.toLocaleString()} advisor${total === 1 ? "" : "s"}`}
          actions={
            <div className="flex flex-wrap items-center gap-1 rounded-xl border border-border/80 bg-card p-1">
              {SORTS.map((s) => (
                <button
                  key={s.key}
                  type="button"
                  onClick={() => {
                    setSort(s.key);
                    fetchRows(1, { sort: s.key });
                  }}
                  className={cn(
                    "rounded-lg px-3 py-1.5 text-sm font-medium transition-colors",
                    sort === s.key
                      ? "bg-primary text-primary-foreground shadow-sm"
                      : "text-muted-foreground hover:bg-accent hover:text-foreground",
                  )}
                >
                  {s.label}
                </button>
              ))}
            </div>
          }
        />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            fetchRows(1);
          }}
          className="mb-5"
        >
          <div className="relative">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/55" />
            <Input
              placeholder="Search advisors by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
        </form>

        {loading && rows.length === 0 ? (
          <Loader className="min-h-[26vh]" />
        ) : rows.length === 0 ? (
          <EmptyPanel icon={Users} title="No advisors found" description="Try a different name." />
        ) : (
          <div
            className={cn(
              "grid grid-cols-1 gap-4 min-[420px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4",
              loading && "pointer-events-none opacity-60",
            )}
          >
            {rows.map((a) => (
              <article
                key={a.id}
                className="flex flex-col overflow-hidden rounded-2xl border border-border/70 bg-white shadow-sm transition-shadow duration-200 hover:shadow-md"
              >
                <button
                  type="button"
                  onClick={() => openProfile(a)}
                  className="relative block h-48 w-full shrink-0 overflow-hidden bg-muted/60"
                  aria-label={`View ${fullName(a)}'s profile`}
                >
                  {/* Absolutely positioned so a tall portrait can't stretch the card. */}
                  {a.avatar_url ? (
                    <img
                      src={assetUrl(a.avatar_url)!}
                      alt={fullName(a)}
                      className="absolute inset-0 h-full w-full object-cover object-top"
                    />
                  ) : (
                    <span className="absolute inset-0 flex items-center justify-center font-display text-5xl text-foreground/80">
                      {initials(a)}
                    </span>
                  )}
                </button>

                <div className="flex flex-1 flex-col p-4">
                  <div className="flex h-6 min-w-0 items-center gap-1.5">
                    <span className="truncate text-base font-bold text-foreground">{fullName(a)}</span>
                    {a.verified && <BadgeCheck className="h-4 w-4 shrink-0 text-primary" aria-label="Verified" />}
                  </div>
                  <div className="mt-0.5 truncate text-sm text-muted-foreground">
                    BL Advisor · {followersLabel(a.follower_count)}
                  </div>
                  {/* Always reserves two lines so cards with and without a bio line up. */}
                  <p className="mt-2 line-clamp-2 h-10 text-xs leading-5 text-muted-foreground">
                    {a.bio || <span className="text-muted-foreground/60">No bio yet.</span>}
                  </p>

                  <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    <Button variant="outline" onClick={() => openProfile(a)} className="w-full px-2">
                      View Profile
                    </Button>
                    {followButton(a, "w-full px-2")}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}

        <Pagination page={page} totalElements={total} pageSize={PAGE_SIZE} onChange={(p) => fetchRows(p)} />
      </PagePanel>

      {profile && <PeerProfileModal profile={profile} onClose={() => setProfile(null)} followButton={followButton} />}
    </PageShell>
  );
}

function HeroStat({ icon: Icon, label, value }: { icon: typeof Users; label: string; value: number | undefined }) {
  return (
    <div className="public-glass-card rounded-xl p-3.5">
      <div className="flex items-center gap-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        <Icon className="h-3.5 w-3.5 text-primary" />
        {label}
      </div>
      <div className="mt-1.5 text-2xl font-extrabold tracking-tight text-foreground">
        {value === undefined ? "—" : value.toLocaleString()}
      </div>
    </div>
  );
}

function PeerProfileModal({
  profile,
  onClose,
  followButton,
}: {
  profile: PeerProfile;
  onClose: () => void;
  followButton: (a: PeerAdvisor, className?: string) => React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-[2px]"
      onClick={onClose}
    >
      <div
        className="relative flex max-h-[90dvh] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 z-10 rounded-full bg-black/25 p-1.5 text-white transition hover:bg-black/40"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="overflow-y-auto">
          <div className="h-20 bg-gold-gradient" />
          <div className="-mt-10 flex flex-col items-center px-6 pb-6 text-center">
            {profile.avatar_url ? (
              <img
                src={assetUrl(profile.avatar_url)!}
                alt={fullName(profile)}
                className="h-20 w-20 rounded-full object-cover shadow-md ring-4 ring-white"
              />
            ) : (
              <div className="flex h-20 w-20 items-center justify-center rounded-full bg-muted font-display text-2xl text-foreground shadow-md ring-4 ring-white">
                {initials(profile)}
              </div>
            )}

            <div className="mt-3 flex items-center gap-1.5">
              <h3 className="heading-bold text-xl text-foreground">{fullName(profile)}</h3>
              {profile.verified && <BadgeCheck className="h-5 w-5 text-primary" aria-label="Verified" />}
            </div>
            <div className="text-sm text-muted-foreground">BL Advisor</div>

            <div className="mt-4 flex gap-8 text-sm">
              <div>
                <div className="text-lg font-bold text-foreground">{profile.follower_count.toLocaleString()}</div>
                <div className="text-muted-foreground">Followers</div>
              </div>
              <div>
                <div className="text-lg font-bold text-foreground">{profile.following_count.toLocaleString()}</div>
                <div className="text-muted-foreground">Following</div>
              </div>
            </div>

            {(profile.email || profile.phone) && (
              <div className="mt-5 flex w-full flex-col gap-2 rounded-xl bg-muted/40 p-4 text-left text-sm">
                {profile.email && (
                  <a href={`mailto:${profile.email}`} className="flex min-w-0 items-center gap-2.5 hover:underline">
                    <Mail className="h-4 w-4 shrink-0 text-primary" />
                    <span className="truncate">{profile.email}</span>
                  </a>
                )}
                {profile.phone && (
                  <a href={`tel:${profile.phone}`} className="flex items-center gap-2.5 hover:underline">
                    <Phone className="h-4 w-4 shrink-0 text-primary" />
                    <span>{profile.phone}</span>
                  </a>
                )}
              </div>
            )}

            <div className="mt-3 w-full rounded-xl bg-muted/40 p-4 text-left">
              <div className="mb-1.5 text-[0.68rem] font-bold uppercase tracking-[0.16em] text-muted-foreground">Bio</div>
              <p className="whitespace-pre-line text-sm leading-6 text-foreground">
                {profile.bio || <span className="text-muted-foreground">This advisor hasn't added a bio yet.</span>}
              </p>
            </div>

            <div className="mt-5 w-full">{followButton(profile, "w-full")}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
